import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery, useMutation } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { Table, Tag, Button, Popconfirm, message } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { GET_SERVICE_LINE_ITEMS } from '../../graphql/serviceLineItems'
import { REMOVE_ORDER_SERVICE_LINE_ITEMS } from '../../graphql/removeOrderServiceLineItems'
import AddServiceLineItemForm from './AddServiceLineItemForm'
import LineItemsWrapper from './lineItems.style'

const ServiceLineItems = ({ orderNumber, i18n }) => {
  const [showAddModal, setShowAddModal] = useState(false)

  const { data, loading, error, refetch } = useQuery(GET_SERVICE_LINE_ITEMS, {
    variables: { orderNumber },
    skip: !orderNumber
  })

  const [removeServiceLineItems, { loading: deleting }] = useMutation(REMOVE_ORDER_SERVICE_LINE_ITEMS, {
    onCompleted: (data) => {
      if (data.removeOrderServiceLineItems.errors) {
        message.error(i18n.t('inquiries:deleteServiceLineItemFailed'))
      } else {
        message.success(i18n.t('inquiries:deleteServiceLineItemSuccess'))
        refetch()
      }
    },
    onError: () => {
      message.error(i18n.t('inquiries:deleteServiceLineItemFailed'))
    }
  })

  const handleDelete = (id) => {
    removeServiceLineItems({
      variables: {
        serviceLineItemIds: [id]
      }
    })
  }

  const handleOpenAddModal = () => {
    setShowAddModal(true)
  }

  const handleCloseAddModal = () => {
    setShowAddModal(false)
  }

  const handleAddSuccess = () => {
    setShowAddModal(false)
    refetch()
  }

  const columns = [
    {
      title: i18n.t('inquiries:lineItemName'),
      dataIndex: 'name',
      key: 'name',
      width: '220px',
      render: (name, record) => (
        <div>
          <div>{name}</div>
          {record.service && (
            <Tag color="blue">{record.service.name}</Tag>
          )}
        </div>
      )
    },
    {
      title: i18n.t('inquiries:quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: '100px'
    },
    {
      title: i18n.t('inquiries:unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: '120px'
    },
    {
      title: i18n.t('inquiries:cost'),
      dataIndex: 'cost',
      key: 'cost',
      width: '140px'
    },
    {
      title: i18n.t('inquiries:unitPrice'),
      dataIndex: 'singlePrice',
      key: 'singlePrice',
      width: '140px'
    },
    {
      title: i18n.t('inquiries:totalPrice'),
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: '140px'
    },
    {
      title: i18n.t('inquiries:action'),
      key: 'action',
      width: '100px',
      render: (record) => (
        <Popconfirm
          title={i18n.t('inquiries:deleteConfirm')}
          onConfirm={() => handleDelete(record.id)}
        >
          <Button type="link" icon={<DeleteOutlined />} danger />
        </Popconfirm>
      )
    }
  ]

  if (error) {
    return <div>Error loading service line items: {error.message}</div>
  }

  const serviceLineItemsEdges = (data && data.order && data.order.serviceLineItems && data.order.serviceLineItems.edges) || []
  const serviceLineItems = serviceLineItemsEdges.map(edge => edge.node)

  return (
    <LineItemsWrapper style={{ marginBottom: 24 }}>
      <div style={{ marginTop: 24, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className='title'>{i18n.t('inquiries:serviceLineItems')}</span>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="small"
          onClick={handleOpenAddModal}
        >
          {i18n.t('inquiries:addServiceLineItem')}
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={serviceLineItems}
        loading={loading || deleting}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: 1000 }}
        locale={{
          emptyText: i18n.t('inquiries:noServiceLineItems')
        }}
      />
      <AddServiceLineItemForm
        visible={showAddModal}
        onCancel={handleCloseAddModal}
        onSuccess={handleAddSuccess}
        orderNumber={orderNumber}
      />
    </LineItemsWrapper>
  )
}

ServiceLineItems.propTypes = {
  orderNumber: PropTypes.string.isRequired,
  i18n: PropTypes.object.isRequired
}

export default withTranslation('inquiries')(ServiceLineItems)
